
import React, { useState } from 'react';
import { UploadIcon } from './icons/UploadIcon';
import { SparkleIcon } from './icons/SparkleIcon';
import { queryDocument } from '../services/vectorService';
import { PaperAirplaneIcon } from './icons/PaperAirplaneIcon';
import { SpinnerIcon } from './icons/SpinnerIcon';
import { ShieldIcon } from './icons/ShieldIcon';
import { QuestionMarkCircleIcon } from './icons/QuestionMarkCircleIcon';
// Add missing DocumentTextIcon import
import { DocumentTextIcon } from './icons/DocumentTextIcon';

const DocumentIntelligence: React.FC = () => {
    const [file, setFile] = useState<File | null>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [extractedText, setExtractedText] = useState<string | null>(null);
    const [chatHistory, setChatHistory] = useState<{role: 'user' | 'ai', text: string}[]>([]);
    const [question, setQuestion] = useState('');
    const [loadingAnswer, setLoadingAnswer] = useState(false);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;
        
        setFile(selectedFile);
        setIsAnalyzing(true);
        
        // Simulation of text extraction logic
        setTimeout(() => {
            setExtractedText("BILLING STATEMENT\nDate: 01/15/2025\nTotal Due: $452.00\nServicer: MOHELA\nAccount: ****1234\n\nNote: Your payment has increased due to annual income recertification. If you believe this is an error, please contact us immediately or submit a new IDR request.");
            setIsAnalyzing(false);
            setChatHistory([{ role: 'ai', text: `I've analyzed ${selectedFile.name}. I've identified the servicer (MOHELA) and your current payment details. How can I help you understand this document?` }]);
        }, 2000);
    };

    const handleAsk = async () => {
        if (!question || !extractedText) return;
        
        const q = question;
        setQuestion('');
        setChatHistory(prev => [...prev, { role: 'user', text: q }]);
        setLoadingAnswer(true);

        const answer = await queryDocument(q, extractedText);
        
        setLoadingAnswer(false);
        setChatHistory(prev => [...prev, { role: 'ai', text: answer || "I couldn't find that in the document." }]);
    };

    return (
        <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-sm flex flex-col">
            <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                <div>
                    <h3 className="text-2xl font-black text-gray-900 flex items-center gap-3 mb-4 uppercase tracking-tighter">
                        <div className="p-2 bg-blue-100 rounded-lg"><SparkleIcon className="w-6 h-6 text-blue-600" /></div>
                        Smart Document Reader
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4 font-medium">
                        Student loan letters and bills are designed to be confusing. Use our **Smart Reader** to translate legalese into plain English. 
                    </p>
                    <div className="space-y-4">
                        <div className="flex gap-3">
                            <div className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">1</div>
                            <p className="text-sm text-gray-600"><strong>Upload:</strong> Drop any bill, servicer letter, or contract (PDF or JPG).</p>
                        </div>
                        <div className="flex gap-3">
                            <div className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">2</div>
                            <p className="text-sm text-gray-600"><strong>Analyze:</strong> Our AI reads the fine print, extracting dates, amounts, and hidden terms.</p>
                        </div>
                        <div className="flex gap-3">
                            <div className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">3</div>
                            <p className="text-sm text-gray-600"><strong>Chat:</strong> Ask questions like "When is my deadline?" or "Is this an approval?"</p>
                        </div>
                    </div>
                </div>

                <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100 space-y-4">
                    <h4 className="font-bold text-blue-900 flex items-center gap-2 text-sm uppercase tracking-widest">
                        <ShieldIcon className="w-4 h-4" />
                        Safe & Private
                    </h4>
                    <p className="text-xs text-blue-800 font-medium">
                        Documents uploaded here are processed in your browser's secure memory and are **never** shared with third parties or used for training. They are discarded as soon as you close your browser tab.
                    </p>
                    <div className="pt-2 border-t border-blue-200">
                        <p className="text-[10px] font-black text-blue-600 uppercase mb-2 tracking-tighter">Try asking:</p>
                        <ul className="text-[11px] text-blue-800 space-y-1 font-bold">
                            <li className="flex items-center gap-1"><QuestionMarkCircleIcon className="w-3 h-3" /> "What is my new monthly payment?"</li>
                            <li className="flex items-center gap-1"><QuestionMarkCircleIcon className="w-3 h-3" /> "What does the second paragraph mean?"</li>
                            <li className="flex items-center gap-1"><QuestionMarkCircleIcon className="w-3 h-3" /> "What action do I need to take?"</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="border-t border-gray-100 pt-8">
                {!file ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-12 text-center bg-gray-50 hover:bg-gray-100 transition-all cursor-pointer relative group">
                        <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleFileUpload} accept=".pdf,.png,.jpg" />
                        <div className="flex flex-col items-center">
                            <div className="p-4 bg-white rounded-full shadow-md mb-4 group-hover:scale-110 transition-transform">
                                <UploadIcon className="w-8 h-8 text-blue-600" />
                            </div>
                            <p className="text-lg font-bold text-gray-900">Drop your document here</p>
                            <p className="text-sm text-gray-500 mt-1">PDF, PNG, and JPG supported (Max 10MB)</p>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col h-[600px] bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-inner">
                        <div className="bg-gray-50 px-6 py-3 border-b flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                                <span className="font-bold text-sm text-gray-700 truncate max-w-xs">{file.name}</span>
                            </div>
                            <button onClick={() => { setFile(null); setChatHistory([]); setExtractedText(null); }} className="text-xs font-black text-red-600 uppercase hover:underline">Remove Document</button>
                        </div>

                        <div className="flex-grow overflow-y-auto p-6 space-y-6 bg-gray-50/30">
                            {isAnalyzing && (
                                <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-3">
                                    <SpinnerIcon className="w-10 h-10 animate-spin text-blue-600" />
                                    <span className="text-sm font-bold uppercase tracking-widest animate-pulse">Running Neural OCR & Analysis...</span>
                                </div>
                            )}
                            {chatHistory.map((msg, i) => (
                                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                                    <div className={`max-w-[80%] p-4 rounded-2xl shadow-sm text-sm leading-relaxed ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-br-none font-medium' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none font-medium'}`}>
                                        {msg.text}
                                    </div>
                                </div>
                            ))}
                            {loadingAnswer && (
                                <div className="flex justify-start">
                                    <div className="bg-white border border-gray-200 p-4 rounded-2xl shadow-sm">
                                        <div className="flex gap-1.5">
                                            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                                            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce delay-100"></div>
                                            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce delay-200"></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-4 border-t bg-white">
                            <div className="relative flex items-center">
                                <input 
                                    type="text" 
                                    className="w-full p-4 pr-16 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm font-medium transition-all"
                                    placeholder="Ask Yana about this document..."
                                    value={question}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
                                    disabled={isAnalyzing || loadingAnswer}
                                />
                                <button 
                                    onClick={handleAsk}
                                    disabled={!question || isAnalyzing || loadingAnswer}
                                    className="absolute right-2 p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 transition-all shadow-md"
                                >
                                    <PaperAirplaneIcon className="w-5 h-5 transform rotate-90" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DocumentIntelligence;
